import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OWNER_EMAIL = "Paul@paintking.com";

/**
 * "Paint your review on our wall" submission box.
 *
 * Behavior:
 *  - 3, 4, or 5 stars → full review form → POST /api/reviews (pending approval)
 *  - 1 or 2 stars   → transforms into a "How can we make it right?" contact panel
 *                      that opens the visitor's mail client to Paul@paintking.com
 */
export default function ReviewForm() {
    const [rating, setRating] = useState(0);
    const [hover, setHover] = useState(0);
    const [name, setName] = useState("");
    const [city, setCity] = useState("");
    const [quote, setQuote] = useState("");
    const [issue, setIssue] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [status, setStatus] = useState(null); // "success" | "error" | null
    const [error, setError] = useState("");
    // Preserved after submit so the "also post on Google" nudge can use them.
    const [lastRating, setLastRating] = useState(0);
    const [lastQuote, setLastQuote] = useState("");
    const [copied, setCopied] = useState(false);

    const isLow = rating > 0 && rating < 3;
    const isHigh = rating >= 3;

    const submitReview = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        setError("");
        try {
            await axios.post(`${API}/reviews`, {
                name: name.trim(),
                city: city.trim(),
                rating,
                quote: quote.trim(),
            });
            setLastRating(rating);
            setLastQuote(quote.trim());
            setStatus("success");
            setName("");
            setCity("");
            setQuote("");
            setRating(0);
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "Something went wrong. Please try again or call 561-698-4044.";
            setError(typeof msg === "string" ? msg : "Please check your entries and try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const copyAndOpenGoogle = async () => {
        try {
            if (navigator.clipboard && lastQuote) {
                await navigator.clipboard.writeText(lastQuote);
                setCopied(true);
                setTimeout(() => setCopied(false), 3000);
            }
        } catch {
            /* clipboard may be blocked — still open Google */
        }
        window.open(
            "https://maps.app.goo.gl/1xWkkj92Sce9TqMV8",
            "_blank",
            "noopener,noreferrer"
        );
    };

    const openMailto = () => {
        const subject = encodeURIComponent(
            "PaintKing — How can we make it right?"
        );
        const bodyLines = [
            "Hi Paul,",
            "",
            `I rated my experience ${rating} of 5 stars.`,
            "",
            "Here's what happened / what could be better:",
            issue.trim() || "(please share details here)",
            "",
            `— ${name.trim() || "A customer"}${
                city.trim() ? `, ${city.trim()}` : ""
            }`,
        ];
        const body = encodeURIComponent(bodyLines.join("\n"));
        window.location.href = `mailto:${OWNER_EMAIL}?subject=${subject}&body=${body}`;
    };

    const StarButton = ({ n }) => {
        const active = (hover || rating) >= n;
        return (
            <button
                type="button"
                data-testid={`review-star-${n}`}
                onClick={() => {
                    setRating(n);
                    setStatus(null);
                    setError("");
                }}
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                className="p-1 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#FFE600]/60 rounded"
                aria-label={`${n} star${n > 1 ? "s" : ""}`}
            >
                <svg
                    viewBox="0 0 24 24"
                    className={`w-9 h-9 md:w-10 md:h-10 transition-colors duration-200 ${
                        active
                            ? "fill-[#FFE600]"
                            : "fill-white/15 hover:fill-white/30"
                    }`}
                >
                    <path d="M12 2l2.9 6.9L22 10l-5.5 4.8L18 22l-6-3.6L6 22l1.5-7.2L2 10l7.1-1.1L12 2z" />
                </svg>
            </button>
        );
    };

    return (
        <motion.section
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
            data-testid="review-form"
            className="mt-16 glass border border-white/15 p-8 md:p-12"
        >
            <div className="grid grid-cols-12 gap-6 md:gap-10 items-start">
                <div className="col-span-12 md:col-span-4">
                    <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50">
                        [ Ch. 05 · b ]
                        <br />
                        Your Turn
                    </div>
                    <h3
                        data-testid="review-form-title"
                        className="font-display uppercase text-4xl md:text-5xl leading-[0.9] tracking-tight mt-4"
                    >
                        Paint your
                        <br />
                        review on
                        <br />
                        <span className="rainbow-text">our wall.</span>
                    </h3>
                    <p className="font-mono text-[12px] leading-relaxed text-white/60 mt-6 max-w-xs">
                        Tap a star to start. Reviews are posted after a quick
                        approval so we can keep the wall real.
                    </p>
                </div>

                <div className="col-span-12 md:col-span-8">
                    {/* Star selector */}
                    <div
                        data-testid="review-star-row"
                        className="flex items-center gap-1 md:gap-2"
                    >
                        {[1, 2, 3, 4, 5].map((n) => (
                            <StarButton key={n} n={n} />
                        ))}
                        <div className="ml-4 font-mono text-[11px] tracking-[0.28em] uppercase text-white/50">
                            {rating === 0
                                ? "Tap to rate"
                                : `${rating}.0 selected`}
                        </div>
                    </div>

                    {/* Success state (approval pending) */}
                    <AnimatePresence mode="wait">
                        {status === "success" && (
                            <motion.div
                                key="success"
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                data-testid="review-success"
                                className="mt-8 border border-[#FFE600]/50 bg-[#FFE600]/[0.06] p-6 md:p-8"
                            >
                                <div className="font-display uppercase text-2xl md:text-3xl">
                                    Thanks — brush is up.
                                </div>
                                <p className="font-mono text-[13px] text-white/80 mt-2 leading-relaxed">
                                    Your review is pending a quick approval
                                    from Paul. It'll land on the wall shortly.
                                </p>

                                {lastRating === 5 && (
                                    <div
                                        data-testid="google-nudge"
                                        className="mt-6 pt-6 border-t border-[#FFE600]/30"
                                    >
                                        <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFE600]">
                                            One more favor?
                                        </div>
                                        <h5 className="font-display uppercase text-xl md:text-2xl leading-tight tracking-tight mt-2">
                                            Paste it on Google too.
                                        </h5>
                                        <p className="font-mono text-[12px] leading-relaxed text-white/70 mt-2 max-w-xl">
                                            5-star reviews on Google send Paul's
                                            phone ringing with new work. Tap
                                            below — we'll copy your review to
                                            your clipboard and open the Google
                                            page so you can paste and post.
                                        </p>
                                        <div className="flex items-center gap-4 flex-wrap mt-4">
                                            <button
                                                type="button"
                                                data-testid="google-nudge-btn"
                                                onClick={copyAndOpenGoogle}
                                                className="group relative overflow-hidden border border-[#FFE600] px-6 py-3 font-mono text-[11px] tracking-[0.3em] uppercase transition-colors"
                                            >
                                                <span className="relative z-10 group-hover:text-black transition-colors">
                                                    Copy & Post On Google →
                                                </span>
                                                <span className="absolute inset-0 bg-[#FFE600] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
                                            </button>
                                            {copied && (
                                                <span
                                                    data-testid="copied-flag"
                                                    className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#FFE600]"
                                                >
                                                    ✓ Copied to clipboard
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {/* HIGH-rating form (3, 4, 5 stars) */}
                        {isHigh && status !== "success" && (
                            <motion.form
                                key="high"
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                onSubmit={submitReview}
                                data-testid="review-high-form"
                                className="mt-8 space-y-4"
                            >
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <input
                                        data-testid="review-name-input"
                                        type="text"
                                        required
                                        maxLength={60}
                                        placeholder="Your name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full bg-black/40 border border-white/20 focus:border-[#FFE600] focus:outline-none px-4 py-3 font-mono text-[13px] tracking-wide text-white placeholder-white/30 transition-colors"
                                    />
                                    <input
                                        data-testid="review-city-input"
                                        type="text"
                                        required
                                        maxLength={80}
                                        placeholder="City, State"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        className="w-full bg-black/40 border border-white/20 focus:border-[#FFE600] focus:outline-none px-4 py-3 font-mono text-[13px] tracking-wide text-white placeholder-white/30 transition-colors"
                                    />
                                </div>
                                <textarea
                                    data-testid="review-quote-input"
                                    required
                                    minLength={10}
                                    maxLength={600}
                                    rows={4}
                                    placeholder="Tell us how the job went…"
                                    value={quote}
                                    onChange={(e) => setQuote(e.target.value)}
                                    className="w-full bg-black/40 border border-white/20 focus:border-[#FFE600] focus:outline-none px-4 py-3 font-mono text-[13px] leading-relaxed tracking-wide text-white placeholder-white/30 transition-colors resize-none"
                                />
                                {error && (
                                    <div
                                        data-testid="review-error"
                                        className="font-mono text-[12px] text-red-400"
                                    >
                                        {error}
                                    </div>
                                )}
                                <div className="flex items-center justify-between gap-4 flex-wrap">
                                    <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/40">
                                        Posted after approval
                                    </p>
                                    <button
                                        data-testid="review-submit-btn"
                                        type="submit"
                                        disabled={submitting}
                                        className="group relative overflow-hidden border border-white/80 hover:border-[#FFE600] px-6 py-3 font-mono text-[11px] tracking-[0.3em] uppercase transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <span className="relative z-10 group-hover:text-black transition-colors">
                                            {submitting ? "Posting…" : "Post my review →"}
                                        </span>
                                        <span className="absolute inset-0 bg-[#FFE600] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
                                    </button>
                                </div>
                            </motion.form>
                        )}

                        {/* LOW-rating recovery box (1 or 2 stars) */}
                        {isLow && (
                            <motion.div
                                key="low"
                                initial={{ opacity: 0, y: 12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                data-testid="review-low-form"
                                className="mt-8 border border-white/30 bg-white/[0.03] p-6 md:p-8"
                            >
                                <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#FFE600]">
                                    Hold up — we want to fix this
                                </div>
                                <h4 className="font-display uppercase text-2xl md:text-3xl leading-tight tracking-tight mt-3">
                                    How can we make it right?
                                </h4>
                                <p className="font-mono text-[13px] text-white/70 mt-3 leading-relaxed">
                                    Before it hits the wall, Paul wants a shot
                                    at making it right. Tell us what happened
                                    and we'll be in touch personally.
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                                    <input
                                        data-testid="review-low-name-input"
                                        type="text"
                                        maxLength={60}
                                        placeholder="Your name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="w-full bg-black/40 border border-white/20 focus:border-[#FFE600] focus:outline-none px-4 py-3 font-mono text-[13px] tracking-wide text-white placeholder-white/30 transition-colors"
                                    />
                                    <input
                                        data-testid="review-low-city-input"
                                        type="text"
                                        maxLength={80}
                                        placeholder="City, State"
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        className="w-full bg-black/40 border border-white/20 focus:border-[#FFE600] focus:outline-none px-4 py-3 font-mono text-[13px] tracking-wide text-white placeholder-white/30 transition-colors"
                                    />
                                </div>
                                <textarea
                                    data-testid="review-issue-input"
                                    rows={4}
                                    maxLength={800}
                                    placeholder="What went wrong? What would set it right?"
                                    value={issue}
                                    onChange={(e) => setIssue(e.target.value)}
                                    className="w-full mt-4 bg-black/40 border border-white/20 focus:border-[#FFE600] focus:outline-none px-4 py-3 font-mono text-[13px] leading-relaxed tracking-wide text-white placeholder-white/30 transition-colors resize-none"
                                />
                                <div className="flex items-center justify-between gap-4 flex-wrap mt-5">
                                    <p className="font-mono text-[10px] tracking-[0.24em] uppercase text-white/40">
                                        Goes straight to Paul
                                    </p>
                                    <button
                                        data-testid="review-email-btn"
                                        type="button"
                                        onClick={openMailto}
                                        className="group relative overflow-hidden border border-[#FFE600] px-6 py-3 font-mono text-[11px] tracking-[0.3em] uppercase transition-colors"
                                    >
                                        <span className="relative z-10 group-hover:text-black transition-colors">
                                            Email Paul →
                                        </span>
                                        <span className="absolute inset-0 bg-[#FFE600] scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-500" />
                                    </button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </motion.section>
    );
}
